import { useEffect, useState } from "react";
import api, { errorMessage } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Plus, Trash2, Loader2, KeyRound, Mail, UserCog, Users as UsersIcon } from "lucide-react";
import { toast } from "sonner";
import { formatTanggal, roleLabel } from "../lib/format";
import { PageHeader } from "../components/ui/page-header";
import { EmptyState } from "../components/ui/empty-state";

export default function AccountCenterPage() {
    const { user: me } = useAuth();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    const [createOpen, setCreateOpen] = useState(false);
    const [form, setForm] = useState({ email: "", password: "", nama: "", role: "accounting", is_owner: false });
    const [submitting, setSubmitting] = useState(false);

    const [confirmDelete, setConfirmDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const [emailOpen, setEmailOpen] = useState(null);
    const [newEmail, setNewEmail] = useState("");

    const [resetOpen, setResetOpen] = useState(null);
    const [newPassword, setNewPassword] = useState("");

    const [roleOpen, setRoleOpen] = useState(null);
    const [roleForm, setRoleForm] = useState({ role: "accounting", is_owner: false, nama: "" });

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/account/list");
            setItems(data);
        } catch (e) { toast.error(errorMessage(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const openCreate = () => {
        setForm({ email: "", password: "", nama: "", role: "accounting", is_owner: false });
        setCreateOpen(true);
    };

    const doCreate = async () => {
        if (!form.email.trim() || !form.email.includes("@")) return toast.error("Email tidak valid");
        if (!form.password || form.password.length < 6) return toast.error("Password minimal 6 karakter");
        if (!form.nama.trim()) return toast.error("Nama wajib diisi");
        setSubmitting(true);
        try {
            await api.post("/account/create", {
                email: form.email.trim().toLowerCase(),
                password: form.password,
                nama: form.nama.trim(),
                role: form.role,
                is_owner: form.role === "owner" ? true : Boolean(form.is_owner),
            });
            toast.success("Akun ditambahkan");
            setCreateOpen(false);
            load();
        } catch (e) { toast.error(errorMessage(e)); }
        finally { setSubmitting(false); }
    };

    const doDelete = async () => {
        if (!confirmDelete) return;
        setDeleting(true);
        try {
            await api.delete(`/account/${confirmDelete.id}`);
            toast.success("Akun dihapus");
            setConfirmDelete(null);
            load();
        } catch (e) { toast.error(errorMessage(e)); }
        finally { setDeleting(false); }
    };

    const doEmailUpdate = async () => {
        if (!newEmail.trim() || !newEmail.includes("@")) return toast.error("Email tidak valid");
        try {
            await api.put(`/account/${emailOpen.id}/email`, { email: newEmail.trim().toLowerCase() });
            toast.success("Email berhasil diubah");
            setEmailOpen(null); setNewEmail("");
            load();
        } catch (e) { toast.error(errorMessage(e)); }
    };

    const doReset = async () => {
        if (!newPassword || newPassword.length < 6) return toast.error("Password minimal 6 karakter");
        try {
            await api.post(`/account/${resetOpen.id}/reset-password`, { password: newPassword });
            toast.success("Password berhasil di-reset");
            setResetOpen(null); setNewPassword("");
        } catch (e) { toast.error(errorMessage(e)); }
    };

    const doRoleUpdate = async () => {
        try {
            await api.put(`/account/${roleOpen.id}/role`, {
                role: roleForm.role,
                is_owner: roleForm.role === "owner" ? true : Boolean(roleForm.is_owner),
                nama: roleForm.nama.trim() || roleOpen.nama,
            });
            toast.success("Role berhasil diperbarui");
            setRoleOpen(null);
            load();
        } catch (e) { toast.error(errorMessage(e)); }
    };

    const RoleBadge = ({ role }) => (
        <span
            className="text-[10px] font-semibold uppercase px-2 py-1 rounded-md inline-block"
            style={{
                background: role === "owner" ? "var(--apple-blue-soft)" : "var(--bg-subtle)",
                color: role === "owner" ? "var(--apple-blue)" : "var(--text-secondary)",
            }}
        >
            {roleLabel(role)}
        </span>
    );

    const ActionRow = ({ row }) => (
        <div className="flex items-center gap-1 justify-end flex-wrap">
            <button
                onClick={() => { setEmailOpen(row); setNewEmail(row.email || ""); }}
                data-testid={`account-email-btn-${row.id}`}
                className="p-2 rounded-lg hover:bg-[var(--bg-subtle)]"
                style={{ color: "var(--text-secondary)" }}
                title="Ubah Email"
            ><Mail className="w-4 h-4" /></button>
            <button
                onClick={() => { setResetOpen(row); setNewPassword(""); }}
                data-testid={`account-reset-btn-${row.id}`}
                className="p-2 rounded-lg hover:bg-[var(--bg-subtle)]"
                style={{ color: "var(--text-secondary)" }}
                title="Reset Password"
            ><KeyRound className="w-4 h-4" /></button>
            <button
                onClick={() => {
                    setRoleOpen(row);
                    setRoleForm({ role: row.role || "accounting", is_owner: !!row.is_owner, nama: row.nama || "" });
                }}
                data-testid={`account-role-btn-${row.id}`}
                className="p-2 rounded-lg hover:bg-[var(--bg-subtle)]"
                style={{ color: "var(--text-secondary)" }}
                title="Edit Role"
            ><UserCog className="w-4 h-4" /></button>
            {row.id !== me?.id && (
                <button
                    onClick={() => setConfirmDelete(row)}
                    data-testid={`account-delete-btn-${row.id}`}
                    className="p-2 rounded-lg hover:bg-[var(--apple-red-soft)]"
                    style={{ color: "var(--apple-red)" }}
                    title="Hapus"
                ><Trash2 className="w-4 h-4" /></button>
            )}
        </div>
    );

    return (
        <div data-testid="account-center-page">
            <PageHeader
                title="Account Center"
                description="Kelola akun pengguna, email, password, dan hak kepemilikan."
                actions={
                    <Button
                        onClick={openCreate}
                        data-testid="account-add-button"
                        className="rounded-lg gap-2 text-white font-medium"
                        style={{ background: "var(--apple-blue)" }}
                    >
                        <Plus className="w-4 h-4" /> Tambah Akun
                    </Button>
                }
            />

            {/* MOBILE: card list */}
            <div className="md:hidden space-y-2">
                {loading ? (
                    <div className="py-12 flex justify-center">
                        <Loader2 className="w-5 h-5 animate-spin" style={{ color: "var(--apple-blue)" }} />
                    </div>
                ) : items.length === 0 ? (
                    <div className="rounded-xl bg-white" style={{ border: "1px solid var(--border)" }}>
                        <EmptyState
                            icon={UsersIcon}
                            title="Belum ada akun"
                            description="Tambahkan akun pengguna pertama Anda."
                            testid="account-empty-mobile"
                        />
                    </div>
                ) : items.map((row) => (
                    <div
                        key={row.id}
                        className="rounded-xl bg-white p-4"
                        style={{ border: "1px solid var(--border)" }}
                        data-testid={`account-card-${row.id}`}
                    >
                        <div className="flex items-start justify-between gap-2 mb-2">
                            <div className="min-w-0 flex-1">
                                <div className="font-medium text-sm truncate" style={{ color: "var(--text-primary)" }}>
                                    {row.nama}
                                </div>
                                <div className="text-xs truncate" style={{ color: "var(--text-muted)" }}>
                                    {row.email || row.username}
                                </div>
                            </div>
                            <RoleBadge role={row.role} />
                        </div>
                        <div className="flex items-center justify-between text-xs mt-2 pt-2" style={{ borderTop: "1px solid var(--border-soft)" }}>
                            <div style={{ color: "var(--text-muted)" }}>
                                {row.is_owner && <span className="font-semibold" style={{ color: "var(--apple-blue)" }}>✓ Pemilik</span>}
                                {!row.is_owner && <span>Dibuat {formatTanggal(row.created_at)}</span>}
                            </div>
                            <ActionRow row={row} />
                        </div>
                    </div>
                ))}
            </div>

            {/* DESKTOP: table */}
            <div
                className="hidden md:block rounded-xl overflow-hidden"
                style={{ background: "#fff", border: "1px solid var(--border)" }}
            >
                <Table data-testid="account-data-table">
                    <TableHeader>
                        <TableRow>
                            <TableHead className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Email</TableHead>
                            <TableHead className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Nama</TableHead>
                            <TableHead className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Role</TableHead>
                            <TableHead className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Pemilik</TableHead>
                            <TableHead className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Dibuat</TableHead>
                            <TableHead className="text-right text-xs font-semibold uppercase tracking-wider w-56" style={{ color: "var(--text-muted)" }}>Aksi</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading ? (
                            <TableRow><TableCell colSpan={6} className="py-12 text-center">
                                <Loader2 className="w-5 h-5 animate-spin mx-auto" style={{ color: "var(--apple-blue)" }} />
                            </TableCell></TableRow>
                        ) : items.length === 0 ? (
                            <TableRow><TableCell colSpan={6}>
                                <EmptyState icon={UsersIcon} title="Belum ada akun" description="Tambahkan akun pengguna pertama Anda." testid="account-empty-desktop" />
                            </TableCell></TableRow>
                        ) : items.map((row) => (
                            <TableRow key={row.id}>
                                <TableCell className="font-medium" style={{ color: "var(--text-primary)" }}>{row.email || row.username}</TableCell>
                                <TableCell style={{ color: "var(--text-primary)" }}>{row.nama}</TableCell>
                                <TableCell><RoleBadge role={row.role} /></TableCell>
                                <TableCell>
                                    {row.is_owner ? (
                                        <span className="text-[11px] font-semibold" style={{ color: "var(--apple-blue)" }}>✓ Pemilik</span>
                                    ) : (
                                        <span className="text-[11px]" style={{ color: "var(--text-muted)" }}>—</span>
                                    )}
                                </TableCell>
                                <TableCell className="text-sm" style={{ color: "var(--text-muted)" }}>
                                    {formatTanggal(row.created_at)}
                                </TableCell>
                                <TableCell className="text-right"><ActionRow row={row} /></TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>

            {/* Create */}
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                <DialogContent className="md:rounded-2xl md:max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Tambah Akun</DialogTitle>
                        <DialogDescription>Buat akun pengguna baru untuk sistem.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <div>
                            <Label>Email <span style={{ color: "var(--apple-red)" }}>*</span></Label>
                            <Input type="email" inputMode="email" value={form.email}
                                onChange={(e) => setForm({ ...form, email: e.target.value })}
                                data-testid="account-form-email" className="mt-1.5 rounded-lg" placeholder="nama@meranggi.com" />
                        </div>
                        <div>
                            <Label>Nama Lengkap <span style={{ color: "var(--apple-red)" }}>*</span></Label>
                            <Input value={form.nama}
                                onChange={(e) => setForm({ ...form, nama: e.target.value })}
                                data-testid="account-form-nama" className="mt-1.5 rounded-lg" placeholder="Nama tampilan" />
                        </div>
                        <div>
                            <Label>Role</Label>
                            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v, is_owner: v === "owner" })}>
                                <SelectTrigger data-testid="account-form-role" className="mt-1.5 rounded-lg">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="owner">Owner (Full access + Account Center)</SelectItem>
                                    <SelectItem value="accounting">Accounting (Full access, tanpa Account Center)</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Password <span style={{ color: "var(--apple-red)" }}>*</span></Label>
                            <Input type="password" value={form.password}
                                onChange={(e) => setForm({ ...form, password: e.target.value })}
                                data-testid="account-form-password" className="mt-1.5 rounded-lg" placeholder="Min. 6 karakter" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setCreateOpen(false)} className="rounded-lg">Batal</Button>
                        <Button onClick={doCreate} disabled={submitting} data-testid="account-create-submit"
                            className="rounded-lg text-white" style={{ background: "var(--apple-blue)" }}>
                            {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Menyimpan...</> : "Simpan"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete */}
            <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
                <AlertDialogContent className="md:rounded-2xl">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Hapus Akun</AlertDialogTitle>
                        <AlertDialogDescription>
                            Yakin ingin menghapus akun <span className="font-semibold" style={{ color: "var(--text-primary)" }}>{confirmDelete?.email || confirmDelete?.username}</span>?
                            Tindakan ini tidak bisa dibatalkan.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-lg">Batal</AlertDialogCancel>
                        <AlertDialogAction onClick={doDelete} disabled={deleting} data-testid="account-confirm-delete"
                            className="rounded-lg text-white" style={{ background: "var(--apple-red)" }}>
                            {deleting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Menghapus...</> : "Hapus"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Email */}
            <Dialog open={!!emailOpen} onOpenChange={(o) => { if (!o) { setEmailOpen(null); setNewEmail(""); } }}>
                <DialogContent className="md:rounded-2xl">
                    <DialogHeader>
                        <DialogTitle>Ubah Email</DialogTitle>
                        <DialogDescription>Ubah email akun <span className="font-semibold">{emailOpen?.nama}</span>.</DialogDescription>
                    </DialogHeader>
                    <div className="py-2">
                        <Label>Email Baru</Label>
                        <Input type="email" inputMode="email" value={newEmail}
                            onChange={(e) => setNewEmail(e.target.value)}
                            data-testid="account-email-input" className="mt-1.5 rounded-lg" placeholder="nama@meranggi.com" />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => { setEmailOpen(null); setNewEmail(""); }} className="rounded-lg">Batal</Button>
                        <Button onClick={doEmailUpdate} data-testid="account-email-submit"
                            className="rounded-lg text-white" style={{ background: "var(--apple-blue)" }}>Simpan</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Reset password */}
            <Dialog open={!!resetOpen} onOpenChange={(o) => { if (!o) { setResetOpen(null); setNewPassword(""); } }}>
                <DialogContent className="md:rounded-2xl">
                    <DialogHeader>
                        <DialogTitle>Reset Password</DialogTitle>
                        <DialogDescription>Reset password untuk <span className="font-semibold">{resetOpen?.nama}</span>.</DialogDescription>
                    </DialogHeader>
                    <div className="py-2">
                        <Label>Password Baru</Label>
                        <Input type="password" value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            data-testid="account-reset-input" className="mt-1.5 rounded-lg" placeholder="Min. 6 karakter" />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => { setResetOpen(null); setNewPassword(""); }} className="rounded-lg">Batal</Button>
                        <Button onClick={doReset} data-testid="account-reset-submit"
                            className="rounded-lg text-white" style={{ background: "var(--apple-blue)" }}>Reset</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Role */}
            <Dialog open={!!roleOpen} onOpenChange={(o) => { if (!o) setRoleOpen(null); }}>
                <DialogContent className="md:rounded-2xl">
                    <DialogHeader>
                        <DialogTitle>Edit Role / Nama</DialogTitle>
                        <DialogDescription>Perbarui role atau nama untuk <span className="font-semibold">{roleOpen?.email}</span>.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <div>
                            <Label>Nama</Label>
                            <Input value={roleForm.nama}
                                onChange={(e) => setRoleForm({ ...roleForm, nama: e.target.value })}
                                data-testid="account-role-nama" className="mt-1.5 rounded-lg" />
                        </div>
                        <div>
                            <Label>Role</Label>
                            <Select value={roleForm.role} onValueChange={(v) => setRoleForm({ ...roleForm, role: v, is_owner: v === "owner" })}>
                                <SelectTrigger data-testid="account-role-select" className="mt-1.5 rounded-lg">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="owner">Owner</SelectItem>
                                    <SelectItem value="accounting">Accounting</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRoleOpen(null)} className="rounded-lg">Batal</Button>
                        <Button onClick={doRoleUpdate} data-testid="account-role-submit"
                            className="rounded-lg text-white" style={{ background: "var(--apple-blue)" }}>Simpan</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
